import { useState, useEffect } from 'react';
import { FiMail, FiX, FiCheckCircle } from 'react-icons/fi';
import { getAuth } from 'firebase/auth';
import { firebaseApp } from '../../services/firebase/firebase.config';
import { resendVerificationEmail } from '../../services/firebase/auth.service';
import { useAppContext } from '../../store/AppContext';
import { saveUserProfile } from '../../services/userProfile.service';
import { activatePreviewAfterVerification } from '../../services/premiumPreview.service';

const auth = getAuth(firebaseApp);

const DISMISS_KEY = 'coretrak.verifyBannerDismissedAt';
const DAY_MS = 24 * 60 * 60 * 1000;

interface EmailVerificationBannerProps {
  email: string | null;
  premiumPreviewUsed?: boolean;
  isAdmin?: boolean;
}

export function EmailVerificationBanner({ email, premiumPreviewUsed = false, isAdmin = false }: EmailVerificationBannerProps) {
  const { user, applyEntitlement } = useAppContext();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [checking, setChecking] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // Check weekly dismiss TTL
  useEffect(() => {
    try {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || '0');
      if (dismissedAt && (Date.now() - dismissedAt < DAY_MS)) {
        setDismissed(true);
      }
    } catch { /* ignore */ }
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  // Resend verification email
  async function handleResend() {
    setError('');
    setSent(false);
    setSending(true);
    try {
      await resendVerificationEmail();
      setSent(true);
      setCooldownSeconds(60);
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send. Try again.';
      if (message.includes('wait')) {
        const match = message.match(/(\d+)\s+second/);
        if (match) setCooldownSeconds(parseInt(match[1], 10));
      }
      setError(message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setSending(false);
    }
  }

  // "I've verified" — write durable field and trigger backend activation
  async function handleCheckVerified() {
    setChecking(true);
    setError('');
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || !user?.uid) {
        setError('Please sign in again to confirm verification.');
        setTimeout(() => setError(''), 5000);
        return;
      }

      // Write durable verification timestamp — source of truth
      await saveUserProfile(user.uid, { emailVerificationCompletedAt: Date.now() });
      console.log('[BANNER] emailVerificationCompletedAt written');

      // Dispatch event so AppContext updates state and triggers reconciliation
      try { window.dispatchEvent(new CustomEvent('email-verification-completed', { detail: { userId: user.uid } })); } catch { /* ignore */ }

      // Also call backend activation directly
      try {
        const result = await activatePreviewAfterVerification();
        console.log('[BANNER] activatePreviewAfterVerification result:', result.status);
        if (result.status === 'activated' || result.status === 'already_active') {
          applyEntitlement(result);
        }
      } catch (err) {
        console.warn('[BANNER] activation failed (non-fatal):', err);
      }

      setVerified(true);
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
      setTimeout(() => setDismissed(true), 2000);
    } catch {
      setError('Could not check. Try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setChecking(false);
    }
  }

  // Dismiss for 7 days
  function handleDismiss() {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  }

  if (dismissed) return null;

  if (verified) {
    return (
      <div className="bg-green-50 border-b border-green-200 px-4 py-2.5 flex items-center justify-center gap-2">
        <FiCheckCircle className="h-4 w-4 text-green-600 shrink-0" />
        <p className="text-xs text-green-900 font-medium">Email verified!</p>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <FiMail className="h-4 w-4 text-amber-600 shrink-0" />
        <p className="text-xs text-amber-900 truncate">
          {sent ? 'Verification email sent! Check your inbox.'
            : error ? error
            : (!premiumPreviewUsed && !isAdmin)
              ? `Verify your email to unlock 48 hours of Trak${email ? ` (${email})` : ''}`
              : `Please verify your email${email ? ` (${email})` : ''}`}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {!sent && (
          <>
            <button onClick={handleCheckVerified} disabled={checking}
              className="text-xs font-medium text-green-800 bg-green-100 hover:bg-green-200 rounded px-2 py-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {checking ? '...' : "I've verified"}
            </button>
            <button onClick={handleResend} disabled={sending || cooldownSeconds > 0}
              className="text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded px-2 py-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {sending ? '...' : cooldownSeconds > 0 ? `${cooldownSeconds}s` : 'Resend'}
            </button>
          </>
        )}
        <button onClick={handleDismiss} className="text-amber-400 hover:text-amber-600 transition-colors p-0.5" aria-label="Dismiss">
          <FiX className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
